<script lang="ts" setup>
import { watch } from 'vue'
import { useForm } from 'vee-validate'
import { SEARCH_GAME_SIDE_BAR_LIMIT } from '~/constants'
import { PROVIDER_NAME_MAP, LOBBY_NAME_MAP } from '~/resources/game'
import { KeyEnum, RoutesEnum } from '~/enums'
import { useSearch } from '~/composables/game/use-search'

interface SearchSuggestions {
  key: string | undefined
  title: string
  onClick: () => Promise<void>
}

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
})
const { isMobile } = useDevice()
const { store, keywordSearch } = useSearch()
const authStore = useAuthStore()
const { playCasinoGame } = useCasino({
  gameLimit: SEARCH_GAME_SIDE_BAR_LIMIT,
  key: KeyEnum.SidebarSearch,
})

const { onPlayGame } = usePlayGame()
const { allGames, allCasinoGames, fetchAllCasinoGames } = useFetchGames()
const router = useRouter()
const route = useRoute()
const isShowSuggest = ref(false)
const isMenuOpened = ref(false)
const searchKey = ref('')

const { values, handleSubmit, setFieldValue } = useForm({
  initialValues: {
    search: '',
  },
})
watch(
  () => keywordSearch.value,
  (value) => {
    setFieldValue('search', value)
  },
  { immediate: true },
)

onMounted(() => {
  if(!allCasinoGames?.value?.length) {
    fetchAllCasinoGames()
  }
})

const searchSuggestions = computed(() => {
  const games = allGames?.value
    ?.filter((x) =>
      x.title?.toLocaleLowerCase().includes(searchKey.value.toLowerCase()),
    )
    ?.map((game) => {
      const categories = game.category
        ?.map((category) => LOBBY_NAME_MAP[category])
        .filter(Boolean)
        .join(', ')
      return {
        key: game.ID?.toString(),
        title: [
          game.title,
          PROVIDER_NAME_MAP[game.provider ?? ''] ||
            capitalizeFirstCharacter(game.provider ?? ''),
          categories,
        ]
          .filter(Boolean)
          .join(' - '),
        to: undefined,
        onClick: () => onPlayGame(game),
      }
    })
  const casinoGames = allCasinoGames?.value
    ?.filter((x) =>
      x.title?.toLocaleLowerCase().includes(searchKey.value.toLowerCase()),
    )
    ?.map((game) => ({
      key: game.gameId?.toString(),
      title: [
        game.title,
        PROVIDER_NAME_MAP[game.provider ?? ''] ||
          capitalizeFirstCharacter(game.provider ?? ''),
        'Live casino',
      ]
        .filter(Boolean)
        .join(' - '),
      to: undefined,
      onClick: () => playCasinoGame(game),
    }))

  const suggestions = values.search
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      games?.concat(casinoGames as any).slice(0, SEARCH_GAME_SIDE_BAR_LIMIT)
    : []

  return suggestions && (values?.search?.trim()?.length)
    ? hightLightSearchKey(values.search, suggestions as SearchSuggestions[])
    : []
})

const hightLightSearchKey = (
  searchKey: string,
  suggestions: SearchSuggestions[],
) => {
  const highlightClass = 'base-menu-item__title--hightlight-text'

  return suggestions.map((item) => {
    const regex = new RegExp(`(${searchKey})`, 'gi')
    const htmlTitle = item.title.replace(
      regex,
      `<span class="${highlightClass}">$1</span>`,
    )

    return {
      ...item,
      htmlTitle: htmlTitle,
    }
  })
}

const onSearchChange = (search: string) => {
  if (!search) {
    store.resetSearchText()
  }
}

const onFocus = () => {
  isShowSuggest.value = true
}

const updateFocused = () => {
  isShowSuggest.value = false
}

const onSubmit = handleSubmit((values) => {
  if (!values.search.trim()) {
    return
  }
  const myElement = document.activeElement as HTMLElement
  myElement.blur()
  const nextRoute = `${RoutesEnum.Search}?q=${values.search.trim()}`
  if (route.path === RoutesEnum.Search) {
    router.replace(nextRoute)
  } else {
    router.push(nextRoute)
  }
  authStore.setIsSidebarOpen(false)
  isMenuOpened.value = false
})

const onClear = () => {
  store.resetSearchText()
  authStore.setIsSidebarOpen(true)
}

const onInput = (search: string) => {
  isMenuOpened.value = Boolean(search.trim())
  searchKey.value = search.trim()
}
</script>
<template>
  <ClientOnly>
    <BaseMenu
      v-model="isMenuOpened"
      :items="searchSuggestions || []"
      :open-on-focus="false"
      location="bottom"
      menu-class="search-suggestions"
      :wrapper-class="`${isShowSuggest && isMobile && 'search-open'}`"
      :custom-class="`${isMobile ? 'search-suggestion-mobile' : ''}`"
      :show-arrow="false"
      is-use-model-value
    >
      <form action="#" @submit.prevent="onSubmit">
        <FormSearchInput
          name="search"
          placeholder="Tìm kiếm trò chơi"
          :class="isCollapsed ? 'form-input--hidden' : ''"
          @on-search-change="onSearchChange"
          @on-focus="onFocus"
          @update-focused="updateFocused"
          @on-clear="onClear"
          @on-input="onInput"
        />
      </form>
    </BaseMenu>
  </ClientOnly>
</template>
